import React, { useState, useEffect } from 'react';
import liff from '@line/liff';
import { buildApiUrl } from '../../utils/apiBase';
import { supabase } from '../../supabaseClient';
import SelectorModal from '../SelectorModal';

const ExpenseModal = ({ isOpen, onClose, onSave, editingExpense, userProfile, savedPayers, paymentMethods, onPaymentMethodsChange, loading, onShowToast, friends, shareCandidates, onDeleteFriend, appUser, onDelete, tripId, defaultCurrency }) => {
  const isTripExpense = !!tripId;
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [currency, setCurrency] = useState('THB');
  const [occurredAt, setOccurredAt] = useState('');
  const [payerMode, setPayerMode] = useState('me'); // 'me' | 'other'
  const [otherPayerName, setOtherPayerName] = useState('');
  const [formError, setFormError] = useState(null);
  const [selectedFriend, setSelectedFriend] = useState(null); // Store selected friend object
  const [isPayerSelectorOpen, setIsPayerSelectorOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('เงินสด');
  const [paymentMethodId, setPaymentMethodId] = useState(null);
  const [isPaymentMethodSelectorOpen, setIsPaymentMethodSelectorOpen] = useState(false);
  const [repeatType, setRepeatType] = useState('once'); // 'once' | 'monthly'
  const [repeatMonths, setRepeatMonths] = useState(1);

  // Helper functions for Date
  const getLocalDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  const formatDateTimeLocal = (isoString) => {
    const date = new Date(isoString);
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return date.toISOString().slice(0, 16);
  };

  const isPayerMe = payerMode === 'me';
  const payerOwnerId = isPayerMe ? appUser?.id : selectedFriend?.id;
  const payerLineId = isPayerMe
    ? (appUser?.line_user_id || userProfile?.userId)
    : selectedFriend?.line_user_id;

  const matchesPayer = (pm) => {
    if (!pm) return false;
    if (pm.name === 'เงินสด') return true;
    if (!payerOwnerId && !payerLineId) return false;
    if (pm.owner_id && payerOwnerId && String(pm.owner_id) === String(payerOwnerId)) return true;
    if (pm.user_id && payerLineId && pm.user_id === payerLineId) return true;
    if (pm.user_id && payerOwnerId && pm.user_id === String(payerOwnerId)) return true;
    return false;
  };

  const filteredPaymentMethods = (paymentMethods || []).filter(matchesPayer);
  const canManageMethods = isPayerMe;

  useEffect(() => {
    const allowedIds = new Set(filteredPaymentMethods.map(pm => String(pm.id)));
    if (paymentMethodId && !allowedIds.has(String(paymentMethodId))) {
      setPaymentMethod('เงินสด');
      setPaymentMethodId(null);
      return;
    }
    if (!paymentMethodId && paymentMethod && paymentMethod !== 'เงินสด') {
      const existsByName = filteredPaymentMethods.some(pm => pm.name === paymentMethod);
      if (!existsByName) {
        setPaymentMethod('เงินสด');
      }
    }
  }, [filteredPaymentMethods, paymentMethodId, paymentMethod]);

  // Reset or Populate form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (editingExpense) {
        setAmount(editingExpense.amount);
        setNote(editingExpense.note || '');
        setCurrency(editingExpense.currency);
        setOccurredAt(formatDateTimeLocal(editingExpense.occurred_at));
        const fallbackName = editingExpense.payment_method || 'เงินสด';
        const matched = paymentMethods?.find(pm =>
            String(pm.id) === String(editingExpense.payment_method_id)
            || (pm.name && pm.name === fallbackName)
        );
        setPaymentMethod(matched?.name || fallbackName);
        setPaymentMethodId(matched?.id || editingExpense.payment_method_id || null);
        setRepeatType('once');
        setRepeatMonths(1);
        
        if (editingExpense.identifier === userProfile?.userId) {
            setPayerMode('me');
            setOtherPayerName('');
            setSelectedFriend(null);
        } else {
            setPayerMode('other');
            
            // พยายามค้นหาข้อมูลเพื่อนจาก payer_user_id เพื่อ Restore selectedFriend
            let foundFriend = null;
            if (editingExpense.payer_user_id && friends) {
                foundFriend = friends.find(f => f.id === editingExpense.payer_user_id);
            }

            if (foundFriend) {
                setOtherPayerName(foundFriend.display_name);
                setSelectedFriend(foundFriend);
            } else {
                setOtherPayerName(editingExpense.identifier);
                setSelectedFriend(null);
            }
        }
      } else {
        setAmount('');
        setNote('');
        setCurrency(defaultCurrency || 'THB');
        setOccurredAt(getLocalDateTime());
        setPayerMode('me');
        setOtherPayerName('');
        setSelectedFriend(null);
        setPaymentMethod('เงินสด');
        setPaymentMethodId(null);
        setRepeatType('once');
        setRepeatMonths(1);
      }
      setFormError(null);
    }
  }, [isOpen, editingExpense, userProfile, defaultCurrency, friends, paymentMethods]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError(null);
    
    if (!note.trim()) {
        setFormError('กรุณาระบุบันทึกช่วยจำ');
        return;
    }

    if (!amount || parseFloat(amount) <= 0) {
        setFormError('กรุณาระบุจำนวนเงินที่ถูกต้อง');
        return;
    }

    if (payerMode === 'other' && !otherPayerName.trim()) {
        setFormError('กรุณาระบุชื่อผู้จ่าย');
        return;
    }

    if (!isTripExpense && repeatType === 'monthly' && (!repeatMonths || repeatMonths < 1 || repeatMonths > 12)) {
        setFormError('กรุณาเลือกระยะเวลารายเดือน 1-12 เดือน');
        return;
    }

    // Send data back to parent
    const finalRepeatType = isTripExpense ? 'once' : repeatType;
    const finalRepeatMonths = isTripExpense ? 1 : repeatMonths;

    onSave({
        payerMode,
        otherPayerName: otherPayerName.trim(),
        selectedFriend, // Pass the friend object if selected
        amount,
        note,
        currency,
        occurredAt,
        paymentMethod,
        paymentMethodId,
        repeatType: finalRepeatType,
        repeatMonths: finalRepeatMonths,
        tripId // ส่ง tripId กลับไป
    });
  };

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-end' }} onClick={onClose}>
      <style>
        {`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}
      </style>
      <div style={{ backgroundColor: 'white', width: '100%', borderRadius: '20px 20px 0 0', padding: '25px', height: '95vh', overflowY: 'auto', animation: 'slideUp 0.3s ease-out', boxShadow: '0 -4px 20px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ color: 'var(--color-primary)', margin: 0, fontSize: '24px' }}>{editingExpense ? '✏️ แก้ไขรายจ่าย' : 'บันทึกรายจ่าย'}</h2>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: 'var(--color-text-subtle)' }}>&times;</button>
        </div>
    
        <form onSubmit={handleSubmit}>
          {formError && <div style={{ color: 'var(--color-danger)', marginBottom: '20px', padding: '12px', backgroundColor: 'var(--color-danger-soft)', borderRadius: '8px', fontSize: '14px' }}>{formError}</div>}
          
          {/* Payer Section */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: 'var(--color-text-muted)', fontWeight: '500' }}>ผู้จ่าย</label>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <button type="button" onClick={() => setPayerMode('me')} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: `1px solid ${payerMode === 'me' ? 'var(--color-primary)' : 'var(--color-border)'}`, backgroundColor: payerMode === 'me' ? 'var(--color-primary-soft)' : 'var(--color-glass-strong)', color: payerMode === 'me' ? 'var(--color-primary)' : 'var(--color-text-muted)', cursor: 'pointer', fontWeight: '500' }}>ฉัน</button>
              <button type="button" onClick={() => setPayerMode('other')} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: `1px solid ${payerMode === 'other' ? 'var(--color-primary)' : 'var(--color-border)'}`, backgroundColor: payerMode === 'other' ? 'var(--color-primary-soft)' : 'var(--color-glass-strong)', color: payerMode === 'other' ? 'var(--color-primary)' : 'var(--color-text-muted)', cursor: 'pointer', fontWeight: '500' }}>คนอื่น</button>
            </div>
            {payerMode === 'me' ? (
              <div style={{ padding: '12px', backgroundColor: 'var(--color-surface-muted)', borderRadius: '8px', color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: '12px', border: '1px solid var(--color-border)' }}>
                {userProfile?.pictureUrl ? <img src={userProfile.pictureUrl} alt="Profile" style={{ width: '40px', height: '40px', borderRadius: '50%' }} /> : <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'var(--color-border)' }}></div>}
                <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{userProfile?.displayName || 'Guest'}</div>
              </div>
            ) : (
              <div>
                <div 
                  onClick={() => setIsPayerSelectorOpen(true)}
                  style={{ 
                    width: '100%', 
                    padding: '14px', 
                    borderRadius: '8px', 
                    border: '1px solid var(--color-border)', 
                    fontSize: '16px', 
                    boxSizing: 'border-box', 
                    color: otherPayerName ? 'var(--color-text)' : 'var(--color-text-subtle)',
                    cursor: 'pointer'
                  }}
                >
                  {otherPayerName || 'ระบุชื่อ...'}
                </div>
              </div>
            )}
          </div>

          {/* Payment Method Section */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: 'var(--color-text-muted)', fontWeight: '500' }}>ช่องทางชำระเงิน</label>
            <div 
              onClick={() => setIsPaymentMethodSelectorOpen(true)}
              style={{ 
                width: '100%', 
                padding: '14px', 
                borderRadius: '8px', 
                border: '1px solid var(--color-border)', 
                fontSize: '16px', 
                boxSizing: 'border-box', 
                color: 'var(--color-text)',
                cursor: 'pointer',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}
            >
              <span>{paymentMethod}</span>
              <span style={{ color: 'var(--color-text-subtle)', fontSize: '12px' }}>▼</span>
            </div>
          </div>

          {/* Note */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: 'var(--color-text-muted)', fontWeight: '500' }}>บันทึกช่วยจำ</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="ค่าอะไร?" required style={{ width: '100%', padding: '14px', borderRadius: '8px', border: '1px solid var(--color-border)', fontSize: '16px', boxSizing: 'border-box', outlineColor: 'var(--color-primary)', minHeight: '100px', resize: 'vertical' }} />
          </div>

          {/* Amount & Currency */}
          <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
            <div style={{ flex: 2 }}>
                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--color-text-muted)', fontWeight: '500' }}>จำนวนเงิน</label>
                <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" required style={{ width: '100%', padding: '14px', borderRadius: '8px', border: '1px solid var(--color-border)', fontSize: '18px', fontWeight: 'bold', boxSizing: 'border-box', outlineColor: 'var(--color-primary)' }} />
            </div>
            <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--color-text-muted)', fontWeight: '500' }}>สกุลเงิน</label>
                <select 
                    value={currency} 
                    onChange={(e) => setCurrency(e.target.value)} 
                    disabled={!!defaultCurrency}
                    style={{ 
                        width: '100%', padding: '14px', borderRadius: '8px', border: '1px solid var(--color-border)', fontSize: '16px', boxSizing: 'border-box', outlineColor: 'var(--color-primary)', 
                        backgroundColor: defaultCurrency ? 'var(--color-surface-muted)' : 'var(--color-glass-strong)',
                        color: defaultCurrency ? 'var(--color-text-muted)' : 'var(--color-text)'
                    }}
                >
                    <option value="THB">THB</option>
                    <option value="USD">USD</option>
                    <option value="JPY">JPY</option>
                    <option value="KRW">KRW</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="CNY">CNY</option>
                    <option value="SGD">SGD</option>
                </select>
            </div>
          </div>

          {/* Date Time */}
          <div style={{ marginBottom: '30px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: 'var(--color-text-muted)', fontWeight: '500' }}>วันและเวลา</label>
            <input type="datetime-local" value={occurredAt} onClick={(e) => e.target.showPicker && e.target.showPicker()} onChange={(e) => setOccurredAt(e.target.value)} required style={{ width: '100%', padding: '14px', borderRadius: '8px', border: '1px solid var(--color-border)', fontSize: '16px', boxSizing: 'border-box', outlineColor: 'var(--color-primary)', cursor: 'pointer' }} />
          </div>

          {/* Recurring - hidden for trip expenses (always once) */}
          {!isTripExpense && (
            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: 'var(--color-text-muted)', fontWeight: '500' }}>ความถี่</label>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <button
                  type="button"
                  disabled={!!editingExpense}
                  onClick={() => setRepeatType('once')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    border: `1px solid ${repeatType === 'once' ? 'var(--color-primary)' : 'var(--color-border)'}`,
                    backgroundColor: repeatType === 'once' ? 'var(--color-primary-soft)' : 'var(--color-glass-strong)',
                    color: repeatType === 'once' ? 'var(--color-primary)' : 'var(--color-text-muted)',
                    cursor: editingExpense ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    opacity: editingExpense ? 0.6 : 1
                  }}
                >
                  ครั้งเดียว
                </button>
                <button
                  type="button"
                  disabled={!!editingExpense}
                  onClick={() => setRepeatType('monthly')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    border: `1px solid ${repeatType === 'monthly' ? 'var(--color-primary)' : 'var(--color-border)'}`,
                    backgroundColor: repeatType === 'monthly' ? 'var(--color-primary-soft)' : 'var(--color-glass-strong)',
                    color: repeatType === 'monthly' ? 'var(--color-primary)' : 'var(--color-text-muted)',
                    cursor: editingExpense ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    opacity: editingExpense ? 0.6 : 1
                  }}
                >
                  รายเดือน
                </button>
              </div>
              {repeatType === 'monthly' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <select
                    value={repeatMonths}
                    onChange={(e) => setRepeatMonths(parseInt(e.target.value, 10))}
                    disabled={!!editingExpense}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid var(--color-border)',
                      fontSize: '16px',
                      boxSizing: 'border-box',
                      outlineColor: 'var(--color-primary)',
                      backgroundColor: editingExpense ? 'var(--color-surface-muted)' : 'var(--color-glass-strong)',
                      color: editingExpense ? 'var(--color-text-muted)' : 'var(--color-text)'
                    }}
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <option key={m} value={m}>{m} เดือน</option>
                    ))}
                  </select>
                </div>
              )}
              {editingExpense && (
                <div style={{ marginTop: '8px', color: 'var(--color-text-subtle)', fontSize: '12px' }}>
                  ตั้งค่ารายเดือนใช้ได้เฉพาะการสร้างรายการใหม่
                </div>
              )}
            </div>
          )}

          {/* Submit Button */}
          <button 
            type="submit" 
            disabled={loading} 
            style={{ 
              width: '100%', 
              padding: '16px', 
              backgroundColor: 'var(--color-primary)', backgroundImage: 'var(--color-primary-gradient)', 
              color: 'white', 
              border: 'none', 
              borderRadius: '8px', 
              fontSize: '16px', 
              fontWeight: 'bold', 
              cursor: 'pointer', 
              boxShadow: '0 4px 10px rgba(var(--color-primary-rgb), 0.2)',
              transition: 'all 0.2s'
            }}
          >
            {loading ? 'กำลังบันทึก...' : (editingExpense ? 'อัปเดต' : 'บันทึก')}
          </button>

          {editingExpense && (
            <button
                type="button"
                onClick={() => onDelete(editingExpense.id)}
                style={{
                    width: '100%',
                    padding: '16px',
                    backgroundColor: 'var(--color-danger-soft)',
                    color: 'var(--color-danger)',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    marginTop: '12px',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                    transition: 'all 0.2s'
                }}
            >
                ลบรายการ
            </button>
          )}
        </form>
      </div>

      {isPayerSelectorOpen && (
        <SelectorModal 
            initialValue={otherPayerName}
            options={savedPayers}
            friends={friends}
            onDeleteFriend={onDeleteFriend}
            onSelect={(val, friendObj) => {
                setOtherPayerName(val);
                setSelectedFriend(friendObj || null);
                setIsPayerSelectorOpen(false);
            }}
            onClose={() => setIsPayerSelectorOpen(false)}
        />
      )}

      {isPaymentMethodSelectorOpen && (
        <PaymentMethodSelectorModal 
            initialValue={paymentMethod}
            initialId={paymentMethodId}
            options={filteredPaymentMethods}
            userProfile={userProfile}
            appUser={appUser}
            friends={shareCandidates || friends}
            tripId={tripId}
            canManage={canManageMethods}
            onSelect={(val, id) => {
                setPaymentMethod(val);
                setPaymentMethodId(id || null);
                setIsPaymentMethodSelectorOpen(false);
            }}
            onClose={() => setIsPaymentMethodSelectorOpen(false)}
            onAddSuccess={onPaymentMethodsChange}
            onShowToast={onShowToast}
        />
      )}
    </div>
  );
};

const PaymentMethodSelectorModal = ({ initialValue, initialId, options, onSelect, onClose, userProfile, appUser, friends = [], tripId, onAddSuccess, onShowToast, canManage = true }) => {
    const [isAdding, setIsAdding] = useState(false);
    const [newCardName, setNewCardName] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editCardName, setEditCardName] = useState('');
    const [isSavingCard, setIsSavingCard] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [cardToDelete, setCardToDelete] = useState(null);
    const [sharedFriendIds, setSharedFriendIds] = useState([]);
    const [editSharedFriendIds, setEditSharedFriendIds] = useState([]);
    const [isShareRemoveModalOpen, setIsShareRemoveModalOpen] = useState(false);
    const [shareRemoveTarget, setShareRemoveTarget] = useState(null);
    const [isShareSelectorOpen, setIsShareSelectorOpen] = useState(false);
    const [shareSelectorMode, setShareSelectorMode] = useState('add'); // 'add' | 'edit'
    const ownerId = appUser?.id;

    const resetAddShareState = () => {
        setSharedFriendIds([]);
    };

    const normalizeId = (value) => (value == null ? null : String(value));
    const toggleFriend = (current, friendId) => {
        const nextId = normalizeId(friendId);
        if (!nextId) return current;
        const normalized = current.map(id => String(id));
        return normalized.includes(nextId)
            ? normalized.filter(id => id !== nextId)
            : [...normalized, nextId];
    };

    const getFriendName = (friend) => friend?.display_name || friend?.username || 'เพื่อน';
    const getFriendInitial = (friend) => {
        const name = getFriendName(friend).trim();
        return name ? name.charAt(0).toUpperCase() : '?';
    };

    const requestRemoveShare = (friend, mode) => {
        const friendId = normalizeId(friend?.id);
        if (!friendId) return;
        setShareRemoveTarget({ id: friendId, name: getFriendName(friend), mode });
        setIsShareRemoveModalOpen(true);
    };

    const confirmRemoveShare = () => {
        if (!shareRemoveTarget) return;
        if (shareRemoveTarget.mode === 'edit') {
            setEditSharedFriendIds(prev => prev.map(id => String(id)).filter(id => id !== String(shareRemoveTarget.id)));
        } else {
            setSharedFriendIds(prev => prev.map(id => String(id)).filter(id => id !== String(shareRemoveTarget.id)));
        }
        setIsShareRemoveModalOpen(false);
        setShareRemoveTarget(null);
    };

    const resolveOwnerName = (pm) => {
        if (pm.owner_name) return pm.owner_name;
        if (pm.owner_id) {
            const owner = friends.find(f => f.id === pm.owner_id);
            return owner?.display_name || null;
        }
        if (pm.user_id) {
            const owner = friends.find(f => f.line_user_id === pm.user_id);
            return owner?.display_name || null;
        }
        return null;
    };

    const SharePicker = ({ selectedIds, setSelectedIds, mode }) => {
        const selectedFriends = selectedIds
            .map(id => friends.find(f => String(f.id) === String(id)))
            .filter(Boolean);

        return (
            <div style={{ borderTop: '1px dashed var(--color-border)', paddingTop: '10px' }}>
                <div style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '6px' }}>
                    แชร์ให้เพื่อน (เลือกได้หลายคน)
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                    {selectedFriends.length === 0 ? (
                        <div style={{ fontSize: '12px', color: 'var(--color-text-subtle)' }}>ยังไม่ได้เลือกเพื่อน</div>
                    ) : (
                        selectedFriends.map(friend => (
                            <button
                                key={friend.id}
                                type="button"
                                onClick={() => requestRemoveShare(friend, mode)}
                                title="ลบจากการแชร์"
                                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                            >
                                <div style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    border: '2px solid var(--color-primary-soft)',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
                                }}>
                                    {friend.picture_url ? (
                                        <img src={friend.picture_url} alt={getFriendName(friend)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <div style={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: 'var(--color-surface-muted)',
                                            color: 'var(--color-text)',
                                            fontSize: '14px',
                                            fontWeight: '600'
                                        }}>
                                            {getFriendInitial(friend)}
                                        </div>
                                    )}
                                </div>
                            </button>
                        ))
                    )}
                </div>

                <button
                    type="button"
                    onClick={() => {
                        setShareSelectorMode(mode);
                        setIsShareSelectorOpen(true);
                    }}
                    style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '10px',
                        border: '1px solid var(--color-border)',
                        backgroundColor: 'var(--color-glass-strong)',
                        color: 'var(--color-text)',
                        fontSize: '14px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                >
                    <span>เลือกเพื่อนที่จะแชร์ ({selectedFriends.length} คน)</span>
                    <span style={{ color: 'var(--color-text-subtle)' }}>▼</span>
                </button>

                {tripId && (
                    <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--color-text-subtle)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: '16px', height: '16px', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--color-border)', color: 'var(--color-text)', fontSize: '11px', fontWeight: '700' }}>
                            i
                        </span>
                        บัตรนี้จะถูกแชร์เฉพาะเพื่อนในทริปนี้เท่านั้น
                    </div>
                )}
            </div>
        );
    };


    const handleAddCard = async () => {
        if (!canManage) return;
        if (!newCardName.trim()) return;
        if (isSavingCard) return;
        if (!ownerId) {
            if (onShowToast) onShowToast('ยังไม่พบข้อมูลผู้ใช้ในระบบ', 'error');
            return;
        }
        const normalized = newCardName.trim().toLowerCase();
        const hasDuplicate = (options || []).some(p => {
            const isOwner = p.owner_id === ownerId || (p.user_id && (p.user_id === userProfile?.userId || p.user_id === String(ownerId)));
            if (!isOwner) return false;
            return (p.name || '').trim().toLowerCase() === normalized;
        });
        if (hasDuplicate) {
            if (onShowToast) onShowToast('มีบัตรชื่อนี้อยู่แล้ว', 'error');
            return;
        }
        
        try {
            const accessToken = liff?.getAccessToken?.();
            if (!accessToken) {
                if (onShowToast) onShowToast('ไม่พบ LINE access token', 'error');
                return;
            }
            setIsSavingCard(true);
            const legacyLineId = userProfile?.userId || appUser?.line_user_id || null;
            const { data: created, error } = await supabase
                .from('payment_methods')
                .insert([{ 
                    name: newCardName.trim(), 
                    type: 'credit_card',
                    owner_id: ownerId,
                    user_id: legacyLineId
                }])
                .select()
                .single();
            
            if (error) throw error;

            const newMethodId = created?.id;
            if (newMethodId) {
                await fetch(buildApiUrl('/api/payment-method-shares'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        accessToken,
                        paymentMethodId: newMethodId,
                        sharedToUserIds: sharedFriendIds
                    })
                }).then(async (res) => {
                    if (!res.ok) {
                        const text = await res.text();
                        throw new Error(text || 'Share update failed');
                    }
                });

                if (tripId) {
                    const tripRes = await fetch(buildApiUrl('/api/trip-payment-methods'), {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            accessToken,
                            action: 'upsert',
                            tripId,
                            paymentMethodId: newMethodId
                        })
                    });
                    if (!tripRes.ok) {
                        const text = await tripRes.text();
                        throw new Error(text || 'Trip share update failed');
                    }
                }
            }
            
            if (onAddSuccess) await onAddSuccess(); // Refresh list in parent
            onSelect(newCardName.trim(), newMethodId); // Select new card
            setIsAdding(false);
            setNewCardName('');
            resetAddShareState();
            if (onShowToast) onShowToast('เพิ่มบัตรเรียบร้อย');
        } catch (error) {
            console.error('Error adding payment method:', error);
            if (onShowToast) onShowToast('ไม่สามารถเพิ่มบัตรได้', 'error');
        } finally {
            setIsSavingCard(false);
        }
    };

    const handleEditCard = async (id) => {
        if (!canManage) return;
        if (!editCardName.trim()) return;
        const normalized = editCardName.trim().toLowerCase();
        const hasDuplicate = (options || []).some(p => {
            if (p.id === id) return false;
            const isOwner = p.owner_id === ownerId || (p.user_id && (p.user_id === userProfile?.userId || p.user_id === String(ownerId)));
            if (!isOwner) return false;
            return (p.name || '').trim().toLowerCase() === normalized;
        });
        if (hasDuplicate) {
            if (onShowToast) onShowToast('มีบัตรชื่อนี้อยู่แล้ว', 'error');
            return;
        }
        try {
            const accessToken = liff?.getAccessToken?.();
            if (!accessToken) {
                if (onShowToast) onShowToast('ไม่พบ LINE access token', 'error');
                return;
            }
            const { error } = await supabase
                .from('payment_methods')
                .update({ name: editCardName.trim() })
                .eq('id', id);
            
            if (error) throw error;

            await fetch(buildApiUrl('/api/payment-method-shares'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    accessToken,
                    paymentMethodId: id,
                    sharedToUserIds: editSharedFriendIds
                })
            }).then(async (res) => {
                if (!res.ok) {
                    const text = await res.text();
                    throw new Error(text || 'Share update failed');
                }
            });

            if (tripId) {
                const tripRes = await fetch(buildApiUrl('/api/trip-payment-methods'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        accessToken,
                        action: 'upsert',
                        tripId,
                        paymentMethodId: id
                    })
                });
                if (!tripRes.ok) {
                    const text = await tripRes.text();
                    throw new Error(text || 'Trip share update failed');
                }
            }
            
            if (onAddSuccess) await onAddSuccess();
            setEditingId(null);
            if (onShowToast) onShowToast('แก้ไขบัตรเรียบร้อย');
        } catch (error) {
            console.error('Error updating payment method:', error);
            if (onShowToast) onShowToast('ไม่สามารถแก้ไขบัตรได้', 'error');
        }
    };

    const handleDeleteClick = (id, name) => {
        setCardToDelete({ id, name });
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!canManage) return;
        if (!cardToDelete) return;
        try {
            const { error } = await supabase
                .from('payment_methods')
                .delete()
                .eq('id', cardToDelete.id);
            
            if (error) throw error;
            
            if (onAddSuccess) {
                await onAddSuccess();
            }
            if (onShowToast) onShowToast('ลบบัตรเรียบร้อย');
        } catch (error) {
            console.error('Error deleting payment method:', error);
            if (onShowToast) onShowToast('ไม่สามารถลบบัตรได้', 'error');
        } finally {
            setIsDeleteModalOpen(false);
            setCardToDelete(null);
        }
    };

    const activeSelectedIds = shareSelectorMode === 'edit' ? editSharedFriendIds : sharedFriendIds;
    const setActiveSelectedIds = shareSelectorMode === 'edit' ? setEditSharedFriendIds : setSharedFriendIds;

    return (
        <div
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-end' }}
            onClick={(e) => {
                e.stopPropagation();
                onClose();
            }}
        >
            <div style={{ backgroundColor: 'var(--color-glass-strong)', width: '100%', borderRadius: '20px 20px 0 0', padding: '20px', maxHeight: '80vh', overflowY: 'auto', animation: 'slideUp 0.3s ease-out' }} onClick={e => e.stopPropagation()}>
                <style>
                    {`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}
                </style>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--color-text)' }}>เลือกช่องทางชำระเงิน</h3>
                    <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: '24px', color: 'var(--color-text-subtle)', cursor: 'pointer' }}>&times;</button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {options && options.map(p => {
                        const isOwner = p.owner_id === ownerId || (p.user_id && (p.user_id === userProfile?.userId || p.user_id === String(ownerId)));
                        const isSystem = !p.owner_id && !p.user_id;
                        const access = isSystem ? 'system' : (p.access || (isOwner ? 'owner' : (p.shared_in_trip ? 'trip' : 'shared')));
                        const ownerName = resolveOwnerName(p);
                        return (
                        <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {editingId === p.id ? (
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px', border: '1px solid var(--color-primary)', borderRadius: '12px', backgroundColor: 'var(--color-surface-muted)' }}>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <input 
                                            type="text" 
                                            value={editCardName} 
                                            onChange={(e) => setEditCardName(e.target.value)} 
                                            style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid var(--color-border)', fontSize: '16px' }} 
                                        />
                                        <button onClick={() => handleEditCard(p.id)} style={{ padding: '0 12px', backgroundColor: 'var(--color-primary)', backgroundImage: 'var(--color-primary-gradient)', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>✓</button>
                                        <button onClick={() => setEditingId(null)} style={{ padding: '0 12px', backgroundColor: 'var(--color-border)', color: 'var(--color-text-muted)', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>✕</button>
                                    </div>
                                    <SharePicker
                                        selectedIds={editSharedFriendIds}
                                        setSelectedIds={setEditSharedFriendIds}
                                        mode="edit"
                                    />
                                </div>
                            ) : (
                                <>
                                    <button 
                                        onClick={() => onSelect(p.name, p.id)} 
                                        style={{ 
                                            flex: 1,
                                            padding: '15px', 
                                            borderRadius: '12px', 
                                            border: (initialId != null && String(initialId) === String(p.id)) || initialValue === p.name ? '2px solid var(--color-primary)' : '1px solid var(--color-border)', 
                                            backgroundColor: (initialId != null && String(initialId) === String(p.id)) || initialValue === p.name ? 'var(--color-primary-soft)' : 'var(--color-glass-strong)', 
                                            textAlign: 'left', 
                                            fontSize: '16px', 
                                            color: (initialId != null && String(initialId) === String(p.id)) || initialValue === p.name ? 'var(--color-primary)' : 'var(--color-text)', 
                                            fontWeight: (initialId != null && String(initialId) === String(p.id)) || initialValue === p.name ? 'bold' : 'normal', 
                                            display: 'flex', 
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                            <span>{p.name}</span>
                                            {!isOwner && !isSystem && p.name !== 'เงินสด' && (
                                                <span style={{ fontSize: '12px', color: 'var(--color-text-subtle)', fontWeight: 'normal' }}>
                                                    {ownerName ? `(บัตรของ ${ownerName})` : '(บัตรของเพื่อน)'}
                                                </span>
                                            )}
                                            {isOwner && (
                                                <span style={{ fontSize: '12px', color: 'var(--color-text-subtle)', fontWeight: 'normal' }}>
                                                    (บัตรของฉัน)
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', gap: '5px' }}>
                                            {access === 'system' && p.name !== 'เงินสด' && <span style={{ fontSize: '12px', color: 'var(--color-text-subtle)', backgroundColor: 'var(--color-border)', padding: '2px 6px', borderRadius: '4px' }}>System</span>}
                                            {access === 'shared' && <span style={{ fontSize: '12px', color: 'var(--color-accent)', backgroundColor: 'var(--color-accent-soft)', padding: '2px 6px', borderRadius: '4px' }}>Shared</span>}
                                            {access === 'trip' && <span style={{ fontSize: '12px', color: 'var(--color-primary)', backgroundColor: 'var(--color-primary-soft)', padding: '2px 6px', borderRadius: '4px' }}>Trip</span>}
                                            {p.shared_in_trip && access !== 'trip' && <span style={{ fontSize: '12px', color: 'var(--color-primary)', backgroundColor: 'var(--color-primary-soft)', padding: '2px 6px', borderRadius: '4px' }}>Trip</span>}
                                            {(p.type === 'credit_card' || (!p.type && p.name !== 'เงินสด')) && (
                                                <span style={{ fontSize: '12px', color: 'var(--color-text-subtle)', backgroundColor: 'var(--color-border)', padding: '2px 6px', borderRadius: '4px' }}>Credit</span>
                                            )}
                                        </div>
                                    </button>
                                    
                                    {isOwner && canManage && (
                                        <div style={{ display: 'flex', gap: '4px' }}>
                                            <button 
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    setEditingId(p.id); 
                                                    setEditCardName(p.name);
                                                    setEditSharedFriendIds(p.shared_to_ids || []);
                                                    setShareSelectorMode('edit');
                                                }}
                                                style={{ padding: '6px', border: '1px solid var(--color-border)', borderRadius: '6px', backgroundColor: 'var(--color-glass-strong)', cursor: 'pointer', color: 'var(--color-accent)' }}
                                                title="แก้ไข"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                                            </button>
                                            <button 
                                                onClick={(e) => { e.stopPropagation(); handleDeleteClick(p.id, p.name); }}
                                                style={{ padding: '6px', border: '1px solid var(--color-border)', borderRadius: '6px', backgroundColor: 'var(--color-glass-strong)', cursor: 'pointer', color: 'var(--color-danger)' }}
                                                title="ลบ"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    );
                    })}

                    {canManage && !isAdding ? (
                        <button onClick={() => { setIsAdding(true); resetAddShareState(); }} style={{ padding: '15px', borderRadius: '12px', border: '1px dashed var(--color-primary)', backgroundColor: 'var(--color-glass-strong)', color: 'var(--color-primary)', fontWeight: 'bold', cursor: 'pointer', marginTop: '10px' }}>
                            + เพิ่มบัตรเครดิต
                        </button>
                    ) : canManage ? (
                        <div style={{ marginTop: '10px', padding: '15px', border: '1px solid var(--color-border)', borderRadius: '12px', backgroundColor: 'var(--color-surface-muted)' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'var(--color-text-muted)' }}>ชื่อเรียกบัตร (เช่น KBank ใบหลัก, Citi Rewards)</label>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <input type="text" value={newCardName} onChange={(e) => setNewCardName(e.target.value)} placeholder="ตั้งชื่อที่จำง่าย..." style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid var(--color-border)' }} disabled={isSavingCard} />
                                <button onClick={handleAddCard} disabled={isSavingCard} style={{ padding: '0 15px', backgroundColor: 'var(--color-primary)', backgroundImage: 'var(--color-primary-gradient)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: isSavingCard ? 'not-allowed' : 'pointer', opacity: isSavingCard ? 0.7 : 1 }}>{isSavingCard ? 'กำลังบันทึก...' : 'บันทึก'}</button>
                            </div>
                            <SharePicker
                                selectedIds={sharedFriendIds}
                                setSelectedIds={setSharedFriendIds}
                                mode="add"
                            />
                            <button onClick={() => setIsAdding(false)} style={{ marginTop: '10px', background: 'none', border: 'none', color: 'var(--color-text-muted)', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}>ยกเลิก</button>
                        </div>
                    ) : null}
                </div>

                {isShareSelectorOpen && (
                    <SelectorModal
                        type="share"
                        friends={friends}
                        selectedIds={activeSelectedIds}
                        onToggleFriend={(friend) => {
                            if (activeSelectedIds.includes(friend.id)) {
                                requestRemoveShare(friend, shareSelectorMode);
                                return;
                            }
                            setActiveSelectedIds(prev => toggleFriend(prev, friend.id));
                        }}
                        onClose={() => setIsShareSelectorOpen(false)}
                    />
                )}

                {isShareRemoveModalOpen && (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2300 }}>
                        <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '16px', width: '90%', maxWidth: '360px', boxShadow: '0 20px 40px rgba(0,0,0,0.1)', textAlign: 'center' }}>
                            <h3 style={{ margin: '0 0 15px 0', color: 'var(--color-text)' }}>ยืนยันการลบ</h3>
                            <p style={{ color: 'var(--color-text-muted)', marginBottom: '25px' }}>
                                ต้องการลบ <strong>{shareRemoveTarget?.name}</strong> ออกจากการแชร์บัตรนี้ใช่หรือไม่?
                            </p>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
                                <button 
                                    onClick={() => { setIsShareRemoveModalOpen(false); setShareRemoveTarget(null); }}
                                    style={{ padding: '10px 20px', backgroundColor: 'var(--color-glass-strong)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    ยกเลิก
                                </button>
                                <button 
                                    onClick={confirmRemoveShare}
                                    style={{ padding: '10px 20px', backgroundColor: 'var(--color-danger)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 6px 14px rgba(224, 86, 75, 0.35)' }}
                                >
                                    ลบออก
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {isDeleteModalOpen && (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2200 }}>
                        <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '16px', width: '90%', maxWidth: '400px', boxShadow: '0 20px 40px rgba(0,0,0,0.1)', textAlign: 'center' }}>
                            <h3 style={{ margin: '0 0 15px 0', color: 'var(--color-text)' }}>ยืนยันการลบ</h3>
                            <p style={{ color: 'var(--color-text-muted)', marginBottom: '15px' }}>ต้องการลบบัตร <strong>"{cardToDelete?.name}"</strong> ใช่หรือไม่?</p>
                            <p style={{ color: 'var(--color-text-subtle)', fontSize: '12px', marginBottom: '25px' }}>(ประวัติการจ่ายด้วยบัตรนี้จะยังคงอยู่)</p>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
                                <button 
                                    onClick={() => setIsDeleteModalOpen(false)}
                                    style={{ padding: '10px 20px', backgroundColor: 'var(--color-glass-strong)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    ยกเลิก
                                </button>
                                <button 
                                    onClick={confirmDelete}
                                    style={{ padding: '10px 20px', backgroundColor: 'var(--color-danger)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 6px 14px rgba(224, 86, 75, 0.35)' }}
                                >
                                    ลบบัตร
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExpenseModal;
